// @givmo/board — an officer records a person's signed paper form, or corrects their answers from one
// (CBG-YR2-WEB1; the paper road, first-class forever: plan §1.7, the derivation §2.14).
//
// ══ THE FORM IS THE PAPER ROAD'S OWN ════════════════════════════════════════════════════════════
//
// The questions, last year's answers and their keys come from the backend's read of this person's
// questionnaire (`GET …/coi-cycles/{id}/questionnaire?board_member_id=`, CBG-YR2-BE5): the same set
// the paper road takes, `I-5` to a director only, a question turned off at opening absent — so what is
// sent is exactly what the backend asks for, and a carried interest keeps its key. A correction starts
// from the answers it corrects, every one as recorded.
//
// The record names the PERSON'S SEAT as its approver, as every disclosure does: the person disclosed,
// the officer wrote it down. So the note saying where the signed form came from is required (v0's
// rule), and the date is the one written on the form.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError, type BoardCoiResponseOut } from '@givmo/api-client';
import { Button, ErrorMessage, Skeleton, TextField, Textarea, toast } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { todayAsDateInput } from '../../lib/display';
import { useBoardOperations } from '../../board-runtime';
import {
  AnswerForm,
  answersIn,
  draftProblems,
  initialDrafts,
  useAnswerDrafts,
} from './coi-answer-form';
import { DialogFrame } from './coi-parts';
import { useCoiPersonQuestionnaire, useRefreshCoi } from './coi-queries';
import { coiWords as w } from './coi-words';

const SECONDARY = 'font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary';

export function PaperFormDialog({
  orgRef,
  cycleId,
  seatId,
  name,
  correcting,
  onClose,
}: {
  orgRef: string;
  cycleId: string;
  seatId: string;
  name: string;
  /** The answers a correction corrects (the head of their chain), or null for a first answer. */
  correcting: BoardCoiResponseOut | null;
  onClose: () => void;
}) {
  const read = useCoiPersonQuestionnaire(orgRef, cycleId, seatId);
  const form = read.data ?? null;
  const title = correcting === null ? w.paperTitle(name) : w.paperCorrectTitle(name);
  const lead = correcting === null ? w.paperLead : w.paperCorrectLead;

  if (form !== null && (correcting !== null || form.cycle.accepts_answers === true)) {
    return (
      <PaperForm
        key={form.cycle.cycle_id}
        orgRef={orgRef}
        cycleId={cycleId}
        seatId={seatId}
        name={name}
        title={title}
        lead={lead}
        form={form}
        correcting={correcting}
        onClose={onClose}
      />
    );
  }
  return (
    <DialogFrame
      title={title}
      lead={lead}
      testId="coi-paper-dialog"
      width={640}
      onClose={onClose}
      footer={
        <Button appearance="saas" tone="secondary" onClick={onClose} testId="coi-paper-cancel">
          {w.cancel}
        </Button>
      }
    >
      {read.isPending ? (
        <div className="flex flex-col gap-12" data-testid="coi-paper-loading">
          <Skeleton className="h-84 w-full rounded-md" />
          <Skeleton className="h-84 w-full rounded-md" />
        </div>
      ) : form === null ? (
        <ErrorMessage testId="coi-paper-unavailable">{w.questionsUnavailable}</ErrorMessage>
      ) : (
        <p className={SECONDARY} data-testid="coi-paper-closed">
          {w.paperClosed}
        </p>
      )}
    </DialogFrame>
  );
}

function PaperForm({
  orgRef,
  cycleId,
  seatId,
  name,
  title,
  lead,
  form,
  correcting,
  onClose,
}: {
  orgRef: string;
  cycleId: string;
  seatId: string;
  name: string;
  title: string;
  lead: string;
  form: NonNullable<ReturnType<typeof useCoiPersonQuestionnaire>['data']>;
  correcting: BoardCoiResponseOut | null;
  onClose: () => void;
}) {
  const operations = useBoardOperations();
  const refresh = useRefreshCoi(orgRef);
  const { drafts, edit } = useAnswerDrafts(() =>
    initialDrafts(form.questions, form.previous, correcting?.answers ?? undefined),
  );
  const [signedOn, setSignedOn] = useState(() => todayAsDateInput());
  const [note, setNote] = useState('');
  const [scan, setScan] = useState('');
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const problems = tried ? draftProblems(form.questions, drafts) : {};
  const carried = correcting === null && form.previous !== null && form.previous !== undefined;

  const record = useMutation({
    mutationFn: async () => {
      await operations.officerRecordBoardCoiPaperResponse({
        path: { org_ref: orgRef, cycle_id: cycleId },
        query: { board_member_id: seatId },
        body: {
          signed_on: signedOn,
          answers: answersIn(form.questions, drafts),
          provenance_note: note.trim(),
          ...(scan.trim().length > 0 ? { artifact_ref: scan.trim() } : {}),
          ...(correcting !== null ? { corrects_record_id: correcting.record_id } : {}),
        },
        throwOnError: true,
      });
    },
    onSuccess: async () => {
      toast.success(correcting === null ? w.paperRecorded(name) : w.paperCorrected(name));
      await refresh();
      onClose();
    },
    onError: (caught) =>
      setError(isGivmoError(caught) && caught.message.trim() ? caught.message : w.couldNotSave),
  });

  const submit = () => {
    setTried(true);
    setError(null);
    if (Object.keys(draftProblems(form.questions, drafts)).length > 0) return;
    if (note.trim().length === 0 || signedOn.length === 0) return;
    record.mutate();
  };

  return (
    <DialogFrame
      title={title}
      lead={lead}
      testId="coi-paper-dialog"
      width={640}
      onClose={onClose}
      footer={
        <>
          <Button appearance="saas" tone="secondary" onClick={onClose} testId="coi-paper-cancel">
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={record.isPending}
            onClick={submit}
            testId="coi-paper-submit"
          >
            {correcting === null ? w.paperRecord : w.paperCorrect}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-16" data-testid="coi-paper-form">
        {carried ? (
          <p className={SECONDARY} data-testid="coi-paper-carried">
            {w.paperCarried(form.previous?.cycle_label ?? '')}
          </p>
        ) : null}
        <AnswerForm
          questions={form.questions}
          previous={correcting === null ? form.previous : null}
          drafts={drafts}
          edit={edit}
          policy={form.policy ?? null}
          label={form.cycle.label}
          period={form.cycle.period_label}
          problems={problems}
          carried={carried}
          detailsAlways={correcting !== null}
          paperStatement={{ lead: w.paperStatementLead(name), tick: w.paperTicked }}
          testId="coi-paper"
        />
        <TextField
          label={w.paperSignedOn}
          type="date"
          value={signedOn}
          onChange={(event) => setSignedOn(event.target.value)}
          testId="coi-paper-signed-on"
        />
        <Textarea
          label={w.paperNote}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          rows={2}
          maxLength={2000}
          testId="coi-paper-note"
        />
        <FieldNote>{w.paperNoteHelp}</FieldNote>
        {tried && note.trim().length === 0 ? (
          <ErrorMessage testId="coi-paper-note-error">{w.paperNoteHelp}</ErrorMessage>
        ) : null}
        <TextField
          label={w.paperScan}
          value={scan}
          onChange={(event) => setScan(event.target.value)}
          maxLength={512}
          testId="coi-paper-scan"
        />
        <FieldNote>{w.paperScanHelp}</FieldNote>
        {error !== null ? <ErrorMessage testId="coi-paper-error">{error}</ErrorMessage> : null}
      </div>
    </DialogFrame>
  );
}
